/**
 * VIB-2100: husk hvilke seksjoner en bruker har kollapset.
 *
 * Helge, 01.09: «Det hadde vært supert om siden klarer å huske at jeg liker å
 * ha alt annet enn "Mine tildelte oppgaver" lukket.»
 *
 * Ren modul uten React, så reglene kan testes uten å mounte noe.
 *
 * LAGRINGSFORM: én nøkkel per bruker med et objekt av seksjon → åpen/lukket,
 * ikke én localStorage-nøkkel per seksjon. Det holder nøkkelrommet ryddig og
 * gjør at en bruker kan nullstilles i én operasjon.
 *
 * PER BRUKER: preferansen henger på brukeren, ikke på maskinen. To personer som
 * deler en maskin skal ikke arve hverandres sammenslåtte seksjoner, og en
 * admin som impersonerer skal ikke skrive over sitt eget oppsett.
 *
 * ALDRI KASTE: privat nettleservindu, blokkerte site-data og full kvote gir
 * alle unntak fra localStorage. En husket seksjonstilstand er en bekvemmelighet
 * — den skal aldri kunne ta ned siden den pynter på.
 */

const PREFIX = "revehiet:sections:";

/** Seksjonene som husker tilstanden sin. Utvides ved behov. */
export type SectionKey =
  | "my-tasks"
  | "reviewed-tasks"
  | "internal-projects"
  | "monthly-entries";

export type SectionState = Partial<Record<SectionKey, boolean>>;

function storageKey(userId: string): string {
  return `${PREFIX}${userId}`;
}

/** Leser hele preferansesettet for en bruker. Aldri kast, aldri undefined. */
export function readSectionState(userId: string | null | undefined): SectionState {
  if (!userId) return {};
  try {
    const raw = localStorage.getItem(storageKey(userId));
    if (!raw) return {};
    const parsed: unknown = JSON.parse(raw);
    // Fremmed eller korrupt innhold behandles som «ingen preferanse» framfor å
    // bli brukt som om det var vårt — en streng eller et array her ville ellers
    // gitt `state[key]` stille rare verdier.
    if (typeof parsed !== "object" || parsed === null || Array.isArray(parsed)) {
      return {};
    }
    const out: SectionState = {};
    for (const [k, v] of Object.entries(parsed as Record<string, unknown>)) {
      if (typeof v === "boolean") out[k as SectionKey] = v;
    }
    return out;
  } catch {
    return {};
  }
}

/**
 * Er seksjonen åpen? `defaultOpen` brukes når brukeren aldri har rørt den,
 * så eksisterende brukere ser nøyaktig det de så før denne endringen.
 */
export function isSectionOpen(
  state: SectionState,
  key: SectionKey,
  defaultOpen: boolean,
): boolean {
  const stored = state[key];
  return typeof stored === "boolean" ? stored : defaultOpen;
}

/**
 * Skriver én seksjons tilstand og returnerer det nye settet.
 *
 * Leser fersk state fra storage før skriving i stedet for å stole på et
 * innsendt øyeblikksbilde: to seksjoner på samme side har hver sin hook, og
 * en sammenslåing basert på gammel state ville latt den ene overskrive den
 * andre.
 */
export function writeSectionState(
  userId: string | null | undefined,
  key: SectionKey,
  open: boolean,
): SectionState {
  const next: SectionState = { ...readSectionState(userId), [key]: open };
  if (!userId) return next;
  try {
    localStorage.setItem(storageKey(userId), JSON.stringify(next));
  } catch {
    // Skrivingen feilet (privat modus, full kvote). Seksjonen oppfører seg
    // riktig i denne økten, den huskes bare ikke til neste.
  }
  return next;
}
